import * as React from 'react';
import { useEffect, useState } from 'react';
import { Dimensions, View, StyleSheet } from 'react-native';
import { TimePickerInputFormProps } from './types';
import { AvailableValidationRules } from './validation';
import { useTheme, Text } from 'react-native-paper';
import { Border, Color } from '@/GlobalStyles';
import { useFormContext } from 'react-hook-form';
import TimePickerInput from '@/components/CustomRNPaper/Dates/Time/TimePickerInput';
import { useTranslation } from 'react-i18next';
import HelpButton from '@/components/HelpButton';

const FTimePickerInput = React.forwardRef<
  React.ElementType<TimePickerInputFormProps>,
  TimePickerInputFormProps
>((props: TimePickerInputFormProps, ref): React.ReactElement => {
  const theme = useTheme();
  const {
    label,
    labelStyle,
    error,
    onChangeValue,
    style,
    defaultValue,
    containerStyle,
    secureTextEntry,
    keyboardType,
    validators,
    placeholder,
    showAsterix = true,
    clearStateFunc,
    withMargin = true,
    withError = true,
    withHelpButton = false,
    helpFieldName,
    topLabel = false,
    ...inputProps
  } = props;

  let realDefaultValue: Date | undefined =
    inputProps.value !== undefined ? inputProps.value : defaultValue;

  const [inputValue, setInputValue] = useState<Date | undefined>(realDefaultValue);

  const { setError } = useFormContext();

  const { t } = useTranslation();

  if (clearStateFunc) {
    clearStateFunc(() => {
      setInputValue(undefined);
    });
  }

  useEffect(() => {
    setInputValue(realDefaultValue);
  }, [defaultValue, inputProps.value]);

  const required =
    showAsterix && validators && validators.indexOf(AvailableValidationRules.required) > -1 ? (
      <Text style={{ color: theme.colors.secondary }}> *</Text>
    ) : null;

  // Create the label with help button if needed
  const labelWithHelp =
    withHelpButton && (helpFieldName || inputProps.name) ? (
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text>
          {t(label)}
          {required}
        </Text>
        <HelpButton
          fieldName={helpFieldName || inputProps.name || ''}
          size={16}
          style={{ marginLeft: 4 }}
        />
      </View>
    ) : required ? (
      <>
        {t(label)}
        {required}
      </>
    ) : (
      t(label)
    );

  return (
    <>
      <View
        style={[
          styles.container,
          withMargin
            ? {
                marginBottom: 10,
              }
            : null,
          containerStyle,
        ]}
      >
        {topLabel && label ? (
          <View style={styles.topLabelContainer}>
            <Text variant={'labelMedium'} style={styles.topLabelText} numberOfLines={2}>
              {t(label)}
              {required}
            </Text>
            {withHelpButton && (helpFieldName || inputProps.name) ? (
              <HelpButton
                fieldName={helpFieldName || inputProps.name || ''}
                size={16}
                style={{ marginLeft: 4 }}
              />
            ) : null}
          </View>
        ) : null}
        <TimePickerInput
          ref={ref}
          label={topLabel ? undefined : labelWithHelp}
          placeholder={(placeholder || '') + (!label ? required : '')}
          style={[styles.input, style]}
          {...inputProps}
          hasError={error?.message !== undefined}
          hideValidationErrors={true}
          locale={'fr'}
          onChange={(text) => {
            setInputValue(text);
            if (onChangeValue) {
              onChangeValue(text);
            }
          }}
          onValidationError={(error) => {
            setInputValue(undefined);
            if (error) {
              setError(inputProps.name, { message: error, type: 'manual' });
            }
          }}
          theme={{
            roundness: 24,
          }}
          value={inputValue}
          mode="flat"
          dense={true}
          underlineColor={'transparent'}
          activeUnderlineColor={theme.colors.primary}
          underlineStyle={{ height: 0 }}
        />
        {withError && error?.message ? (
          <Text
            variant={'labelMedium'}
            style={{
              color: theme.colors.error,
              marginHorizontal: 18,
              marginTop: 4,
            }}
          >
            {error?.message}
          </Text>
        ) : null}
      </View>
    </>
  );
});

FTimePickerInput.displayName = 'DatePickerInput';

export default FTimePickerInput;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
  },
  topLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    flexWrap: 'nowrap',
  },
  topLabelText: {
    flex: 1,
    flexShrink: 1,
    flexWrap: 'wrap',
  },
  input: {
    height: 48,
    borderWidth: 1,
    borderColor: Color.colorGray_100,
    borderStyle: 'solid',
    borderRadius: Border.br_3xs,
    backgroundColor: Color.blanc,
  },
  accessory: {
    width: Dimensions.get('window').width,
    height: 48,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    backgroundColor: '#F8F8F8',
    paddingHorizontal: 8,
  },
});
